import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import BlogTOC from './components/BlogTOC';
import BlogCard from './components/BlogCard';
import MarkdownRenderer from './components/MarkdownRenderer';
import {
  BlogPage,
  PostPageWrap,
  PostHeader,
  PostHeaderMeta,
  PostMetaSep,
  PostTitle,
  PostSubtitle,
  PostCover,
  PostContent,
  PostAuthor,
  RelatedSection,
  RelatedTitle,
  BlogContainer,
  PostGrid,
  LoadingWrap,
  EmptyState,
} from './styles/BlogStyles';
import {
  getPostBySlug,
  getRelatedPosts,
  getBlogImageUrl,
  getCategoriaLabel,
  formatPostDate,
  estimateReadingTime,
  extractTOC,
  incrementPostViews,
} from '../services/blogService';
import { getPublishedProperties } from '../services/propertyService';
import { MF_BASE } from '../basePath';

// Limpa markdown pra TTS (remove headers, links, bold, code, imagens)
function stripMarkdown(md = '') {
  return md
    .replace(/```[\s\S]*?```/g, '')                  // code blocks
    .replace(/`[^`]*`/g, '')                          // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')             // images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')          // links
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')               // headers
    .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, '$1')     // bold/italic
    .replace(/^>\s?/gm, '')                            // blockquotes
    .replace(/^[-*+]\s+/gm, '')                        // list markers
    .replace(/\|/g, ' ')                                // tables
    .trim();
}

// Quebra texto em sentenças curtas (uma frase por chunk = highlighting fluido).
// Divide primeiro por parágrafos (\n\n), depois cada parágrafo por pontuação,
// preservando títulos como chunks individuais (mesmo sem ponto final).
function splitIntoChunks(md = '') {
  const paragraphs = (md || '')
    .split(/\n{2,}/)
    .map(p => stripMarkdown(p).replace(/\s+/g, ' ').trim())
    .filter(p => p.length > 0);

  const out = [];
  for (const p of paragraphs) {
    const SENT_SEP = String.fromCharCode(1);
    const sentences = p
      .replace(/([.!?])\s+/g, '$1' + SENT_SEP)
      .split(SENT_SEP)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    out.push(...sentences);
  }
  return out;
}

function ArticleTTS({ title, content }) {
  const [supported, setSupported] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const chunksRef = useRef([]);
  const idxRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setSupported(false);
    }
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  if (!supported) return null;

  const handlePlay = () => {
    const synth = window.speechSynthesis;
    if (paused) {
      synth.resume();
      setPaused(false);
      return;
    }
    synth.cancel();
    const titleClean = (title || '').trim();
    const bodyChunks = splitIntoChunks(content);
    // Só prepend title se o conteúdo NÃO começar com o mesmo título (markdown often has # title)
    const firstChunkLower = bodyChunks[0]?.toLowerCase() || '';
    const titleLower = titleClean.toLowerCase();
    const titleAlreadyInBody = !!titleLower && firstChunkLower.startsWith(titleLower.slice(0, Math.min(40, titleLower.length)));
    const all = titleAlreadyInBody || !titleClean
      ? bodyChunks
      : [titleClean + '.', ...bodyChunks];
    chunksRef.current = all;
    idxRef.current = 0;
    setCurrentIdx(0);
    setSpeaking(true);
    // Encadeia: ao terminar uma utterance, dispara a próxima.
    // Implementação: cria todas e fila no synth (ele processa em sequência).
    all.forEach((chunk, i) => {
      const u = new SpeechSynthesisUtterance(chunk);
      u.lang = 'pt-BR';
      u.rate = 1.0;
      u.pitch = 1.0;
      const voices = synth.getVoices();
      const ptVoice = voices.find(v => v.lang === 'pt-BR') || voices.find(v => v.lang?.startsWith('pt'));
      if (ptVoice) u.voice = ptVoice;
      u.onstart = () => {
        idxRef.current = i;
        setCurrentIdx(i);
      };
      u.onend = () => {
        if (i === all.length - 1) {
          setSpeaking(false);
          setPaused(false);
          setCurrentIdx(-1);
        }
      };
      u.onerror = () => { setSpeaking(false); setPaused(false); };
      synth.speak(u);
    });
  };

  const handlePause = () => {
    window.speechSynthesis.pause();
    setPaused(true);
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
    setCurrentIdx(-1);
  };

  const isPlayingActive = speaking && !paused;
  const currentText = currentIdx >= 0 ? chunksRef.current[currentIdx] : '';
  const totalChunks = chunksRef.current.length;
  const progress = totalChunks > 0 && currentIdx >= 0 ? ((currentIdx + 1) / totalChunks) * 100 : 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        padding: '20px 24px',
        background: 'linear-gradient(135deg, #0F1828 0%, #1A2A44 100%)',
        borderRadius: 14,
        margin: '24px 0 32px',
        color: '#fff',
        boxShadow: '0 8px 24px rgba(15, 24, 40, 0.18)',
      }}
    >
      <button
        type="button"
        onClick={isPlayingActive ? handlePause : handlePlay}
        aria-label={isPlayingActive ? 'Pausar narração' : 'Ouvir artigo'}
        style={{
          width: 64, height: 64, borderRadius: '50%',
          background: '#fff', color: '#0F1828',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          transition: 'transform 160ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.06)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {isPlayingActive ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 3 }}>
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <div style={{ flex: 1, lineHeight: 1.4, minWidth: 0 }}>
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          opacity: 0.6,
          marginBottom: 4,
        }}>
          {isPlayingActive ? 'Narrando agora' : paused ? 'Pausado' : 'Modo leitura'}
        </div>
        {speaking && currentText ? (
          <>
            <div style={{
              fontSize: 15,
              lineHeight: 1.5,
              fontWeight: 500,
              maxHeight: 84,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}>
              {currentText}
            </div>
            <div style={{
              marginTop: 10,
              height: 3,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.18)',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: '#fff',
                transition: 'width 280ms ease',
              }} />
            </div>
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
              Frase {currentIdx + 1} de {totalChunks}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              Ouvir o artigo
            </div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
              Narração em pt-BR · acompanhe o texto sendo lido em tempo real
            </div>
          </>
        )}
      </div>
      {speaking && (
        <button
          type="button"
          onClick={handleStop}
          aria-label="Parar narração"
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.25)',
            color: '#fff',
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Parar
        </button>
      )}
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [relatedProps, setRelatedProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setPost(null);

    (async () => {
      const data = await getPostBySlug(slug);
      if (!active) return;
      if (!data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setPost(data);
      setLoading(false);

      // best-effort, não bloqueia
      incrementPostViews(data.id);

      // related posts
      const rel = await getRelatedPosts(data.id, {
        bairro: data.bairro_relacionado,
        categoria: data.categoria,
        limit: 3,
      });
      if (active) setRelated(rel);

      // imóveis relacionados (do mesmo bairro)
      if (data.bairro_relacionado) {
        const props = await getPublishedProperties({
          bairro: data.bairro_relacionado,
        });
        if (active) setRelatedProps((props || []).slice(0, 3));
      }
    })();

    return () => {
      active = false;
    };
  }, [slug]);

  // SEO meta tags + JSON-LD
  useEffect(() => {
    if (!post) return;

    document.title = post.seo_title || `${post.titulo} | MF Real Estate`;

    const setMeta = (name, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', name);
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };
    const setProperty = (prop, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[property="${prop}"]`);
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('property', prop);
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };

    const description = post.seo_description || post.resumo || post.subtitulo || '';
    const image = post.imagem_capa ? getBlogImageUrl(post.imagem_capa) : '';
    const canonical = `https://www.mfrealstate.com.br/mf-real-state/blog/${post.slug}`;
    const ogTitle = post.seo_title || post.titulo;

    setMeta('description', description);

    // Open Graph
    setProperty('og:title', ogTitle);
    setProperty('og:description', description);
    setProperty('og:type', 'article');
    setProperty('og:image', image);
    setProperty('og:url', canonical);
    setProperty('og:site_name', 'MF Real Estate');

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle);
    setMeta('twitter:description', description);
    if (image) setMeta('twitter:image', image);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    // JSON-LD Article + BreadcrumbList
    const existing = document.getElementById('blog-jsonld');
    if (existing) existing.remove();

    const jsonld = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.titulo,
        description: post.seo_description || post.resumo || '',
        image: image || undefined,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: {
          '@type': 'Organization',
          name: post.autor || 'Equipe MF Real Estate',
          url: 'https://www.mfrealstate.com.br/mf-real-state',
        },
        publisher: {
          '@type': 'Organization',
          name: 'MF Real Estate',
          url: 'https://www.mfrealstate.com.br/mf-real-state',
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'MF Real Estate', item: 'https://www.mfrealstate.com.br/mf-real-state' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.mfrealstate.com.br/mf-real-state/blog' },
          { '@type': 'ListItem', position: 3, name: post.titulo, item: canonical },
        ],
      },
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-jsonld';
    script.text = JSON.stringify(jsonld);
    document.head.appendChild(script);

    // FAQ schema (se existir)
    if (post.faq_jsonld && Array.isArray(post.faq_jsonld) && post.faq_jsonld.length > 0) {
      const oldFaq = document.getElementById('blog-faq-jsonld');
      if (oldFaq) oldFaq.remove();
      const faqLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faq_jsonld.map((q) => ({
          '@type': 'Question',
          name: q.question || q.pergunta,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer || q.resposta,
          },
        })),
      };
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.id = 'blog-faq-jsonld';
      faqScript.text = JSON.stringify(faqLd);
      document.head.appendChild(faqScript);
    }

    return () => {
      const j = document.getElementById('blog-jsonld');
      if (j) j.remove();
      const f = document.getElementById('blog-faq-jsonld');
      if (f) f.remove();
    };
  }, [post]);

  if (loading) {
    return (
      <BlogPage>
        <Navbar />
        <LoadingWrap>Carregando…</LoadingWrap>
      </BlogPage>
    );
  }

  if (notFound) {
    return (
      <BlogPage>
        <Navbar />
        <BlogContainer>
          <EmptyState>
            <h3>Artigo não encontrado</h3>
            <p>O artigo que você procura não existe ou foi removido.</p>
            <p style={{ marginTop: 24 }}>
              <a
                href={`${MF_BASE}/blog`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`${MF_BASE}/blog`);
                }}
                style={{ color: '#171717', textDecoration: 'underline' }}
              >
                Voltar para o blog
              </a>
            </p>
          </EmptyState>
        </BlogContainer>
        <Footer />
      </BlogPage>
    );
  }

  if (!post) return null;

  const toc = extractTOC(post.conteudo_md);
  const cover = getBlogImageUrl(post.imagem_capa);

  return (
    <BlogPage>
      <Navbar />

      <PostPageWrap>
        {/* coluna principal — conteúdo */}
        <div>
          <PostHeader>
            <PostHeaderMeta>
              {post.categoria && <span>{getCategoriaLabel(post.categoria)}</span>}
              {post.categoria && post.published_at && <PostMetaSep />}
              {post.published_at && (
                <span>{formatPostDate(post.published_at)}</span>
              )}
              <PostMetaSep />
              <span>{estimateReadingTime(post.conteudo_md)} min de leitura</span>
            </PostHeaderMeta>
            <PostTitle>{post.titulo}</PostTitle>
            {post.subtitulo && <PostSubtitle>{post.subtitulo}</PostSubtitle>}
          </PostHeader>

          {cover && (
            <PostCover>
              <img src={cover} alt={post.titulo} />
            </PostCover>
          )}

          <ArticleTTS title={post.titulo} content={post.conteudo_md} />

          <PostContent>
            <MarkdownRenderer>{post.conteudo_md}</MarkdownRenderer>
          </PostContent>

          <PostAuthor>
            Por <strong>{post.autor || 'Equipe MF Real Estate'}</strong>
          </PostAuthor>
        </div>

        {/* coluna direita — TOC sticky */}
        <BlogTOC items={toc} />
      </PostPageWrap>

      {/* imóveis relacionados (do bairro do artigo) */}
      {relatedProps.length > 0 && (
        <RelatedSection>
          <BlogContainer>
            <RelatedTitle>
              Imóveis em {post.bairro_relacionado}
            </RelatedTitle>
            <PostGrid>
              {relatedProps.map((prop) => (
                <PropertyCard key={prop.id} property={prop} />
              ))}
            </PostGrid>
          </BlogContainer>
        </RelatedSection>
      )}

      {/* posts relacionados */}
      {related.length > 0 && (
        <BlogContainer style={{ paddingTop: 64, paddingBottom: 96 }}>
          <RelatedTitle>Continue lendo</RelatedTitle>
          <PostGrid>
            {related.map((p, i) => (
              <BlogCard key={p.id} post={p} index={i} />
            ))}
          </PostGrid>
        </BlogContainer>
      )}

      <Footer />
    </BlogPage>
  );
}
